import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  Divider,
  Typography,
  useTheme,
} from "@mui/material";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonPrimary } from "src/UI/ButtonPrimary";

export type ConfirmDialogVariant = "primary" | "danger";

export interface ConfirmDialogCstProps {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmVariant?: ConfirmDialogVariant;
  isSubmitting?: boolean;
  submittingLabel?: string;
}

const ConfirmDialogCst: React.FC<ConfirmDialogCstProps> = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  onClose,
  onConfirm,
  confirmVariant = "primary",
  isSubmitting = false,
  submittingLabel,
}) => {
  const theme = useTheme();

  const submittingIcon = isSubmitting ? (
    <CircularProgress size={14} color="inherit" />
  ) : undefined;

  const confirmBg =
    confirmVariant === "danger"
      ? theme.palette.error.main
      : theme.palette.primary.main;
  const confirmBgHover =
    confirmVariant === "danger"
      ? theme.palette.error.dark
      : theme.palette.primary.dark;
  const confirmFg =
    confirmVariant === "danger"
      ? theme.palette.error.contrastText
      : theme.palette.primary.contrastText;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          maxWidth: "600px",
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "16px",
          padding: "32px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            lineHeight: 1.2,
            color: theme.palette.text.primary,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        {typeof description === "string" ? (
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: 1.5,
              color: theme.palette.text.primary,
            }}
          >
            {description}
          </Typography>
        ) : (
          description
        )}
        <Divider flexItem />
        <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
          <ButtonSecondary
            size="md"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ flex: 1 }}
          >
            {cancelLabel}
          </ButtonSecondary>
          <ButtonPrimary
            size="md"
            onClick={onConfirm}
            disabled={isSubmitting}
            startIcon={submittingIcon}
            sx={{
              flex: 1,
              ...(confirmVariant === "danger" && {
                backgroundColor: confirmBg,
                color: confirmFg,
                "&:hover": { backgroundColor: confirmBgHover },
                "&:active": { backgroundColor: confirmBgHover },
              }),
            }}
          >
            {isSubmitting && submittingLabel ? submittingLabel : confirmLabel}
          </ButtonPrimary>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmDialogCst;
